<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { type UploadCustomRequestOptions, type UploadFileInfo } from 'naive-ui';
import type { ContractGenerationTemplateInDb } from '@/api';
import { TemplatesService } from '@/services/contractGeneration/templatesService';
import { extractErrorDetail } from '@/utils/errorHandler';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaFileUpload from '@/components/design/base/GaiaFileUpload.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaDeleteButton from '@/components/design/base/GaiaDeleteButton.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaTextarea from '@/components/design/base/inputs/GaiaTextarea.vue';
import { gaiaAccent, gaiaNeutral } from '@/components/design/themes/tokens';
import prettyBytes from 'pretty-bytes';
import { useGaiaToast } from '@/composables/useGaiaToast';

interface Props {
  show: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'update:show', value: boolean): void;
  (e: 'created', template: ContractGenerationTemplateInDb): void;
}>();

const message = useGaiaToast();

const name = ref<string>('');
const variantName = ref<string>('');
const description = ref<string>('');
const fileRef = ref<UploadFileInfo | null>(null);
const submitting = ref<boolean>(false);
const error = ref<string | null>(null);

const showModal = computed({
  get: () => props.show,
  set: value => {
    emit('update:show', value);
  },
});

const canSubmit = computed(() => {
  return (
    !submitting.value &&
    name.value.trim().length > 0 &&
    variantName.value.trim().length > 0 &&
    fileRef.value?.file instanceof File
  );
});

const pickedFile = computed(() =>
  fileRef.value?.file instanceof File ? fileRef.value.file : null
);

function resetForm(): void {
  name.value = '';
  variantName.value = '';
  description.value = '';
  fileRef.value = null;
  error.value = null;
}

watch(
  () => props.show,
  value => {
    if (value) resetForm();
  }
);

function handleUploadChange(options: { fileList: UploadFileInfo[] }): void {
  fileRef.value = options.fileList.at(-1) ?? null;
}

function removeFile(): void {
  fileRef.value = null;
}

function handleCustomUpload({ onFinish }: UploadCustomRequestOptions): void {
  onFinish();
}

async function submit(): Promise<void> {
  if (!canSubmit.value) return;
  const file = fileRef.value?.file;
  if (!(file instanceof File)) return;

  submitting.value = true;
  error.value = null;

  try {
    const template = await TemplatesService.create(
      file,
      name.value.trim(),
      variantName.value.trim(),
      description.value.trim() || null
    );
    // Validation runs asynchronously on the backend — the variant shows a
    // "Validating template…" badge until the worker finishes.
    message.success('Template created — validating in the background…');
    emit('created', template);
    emit('update:show', false);
  } catch (error_) {
    error.value = extractErrorDetail(error_, 'Failed to create template.');
  } finally {
    submitting.value = false;
  }
}

/**
 * Inline custom properties rather than `v-bind()` — the modal root is a
 * Teleport, so Vue has no root element in place to write `v-bind()` vars onto.
 */
const styleVars = {
  '--tpl-create-card-border': gaiaNeutral.borderCard,
  '--tpl-create-soft-surface': gaiaNeutral.surfaceTint,
  '--tpl-create-accent': gaiaAccent.base,
};
</script>

<template>
  <gaia-modal
    v-model:show="showModal"
    width="520px"
    :mask-closable="!submitting"
    :closable="!submitting"
    :style="styleVars"
  >
    <template #header>
      <n-flex vertical :size="6">
        <gaia-text size="subtitle" weight="semibold" tone="ink">Create template</gaia-text>
        <gaia-text size="row" tone="text">
          Upload a .docx source file — placeholders are extracted automatically.
        </gaia-text>
      </n-flex>
    </template>

    <n-flex vertical :size="14">
      <gaia-banner v-if="error" type="error">{{ error }}</gaia-banner>

      <gaia-form-field label="Template name" required>
        <gaia-input v-model:value="name" placeholder="e.g. Service Agreement" style="width: 100%" />
      </gaia-form-field>

      <!-- Every template starts with one variant, so its name is asked for
           here rather than in a second step. -->
      <gaia-form-field label="Variant name" required hint="a language, a country, a wording">
        <gaia-input
          v-model:value="variantName"
          placeholder="e.g. German, Freelancer (Germany), Male wording"
          style="width: 100%"
        />
      </gaia-form-field>

      <gaia-form-field label="Description" hint="Optional">
        <gaia-textarea
          v-model:value="description"
          placeholder="What is this template for?"
          :autosize="{ minRows: 2, maxRows: 4 }"
          style="width: 100%"
        />
      </gaia-form-field>

      <gaia-form-field label=".docx template file" required>
        <!-- The picked file replaces the dropzone rather than listing under it:
             the flow takes exactly one file. -->
        <div v-if="pickedFile" class="picked-file">
          <svg
            width="15"
            height="15"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            stroke-width="1.4"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="picked-file__icon"
          >
            <path d="M4 2.5h5l3 3v8H4z" />
            <path d="M9 2.5v3h3" />
          </svg>
          <n-flex vertical :size="1" :style="{ minWidth: 0, flex: 1 }">
            <gaia-text truncate size="dense" weight="semibold" tone="ink">
              {{ pickedFile.name }}
            </gaia-text>
            <gaia-text size="micro" tone="faint">{{ prettyBytes(pickedFile.size) }}</gaia-text>
          </n-flex>
          <gaia-delete-button
            intent="remove"
            subject="file"
            :disabled="submitting"
            @click="removeFile"
          />
        </div>

        <gaia-file-upload
          v-else
          variant="dropzone"
          :default-upload="false"
          :multiple="false"
          :custom-request="handleCustomUpload"
          :show-file-list="false"
          accept=".docx"
          @change="handleUploadChange"
        >
          Click or drag a .docx file here
          <template #description>Template placeholders will be extracted automatically.</template>
        </gaia-file-upload>
      </gaia-form-field>
    </n-flex>

    <template #footer>
      <n-flex align="center" :size="8">
        <gaia-text size="micro" tone="faint">
          Validation runs in the background after creation.
        </gaia-text>
        <gaia-button
          size="small"
          :disabled="submitting"
          :style="{ marginLeft: 'auto' }"
          @click="emit('update:show', false)"
        >
          Cancel
        </gaia-button>
        <gaia-button
          type="primary"
          size="small"
          :loading="submitting"
          :disabled="!canSubmit"
          @click="submit"
        >
          Create template
        </gaia-button>
      </n-flex>
    </template>
  </gaia-modal>
</template>

<style scoped>
/* Colours come through the styleVars map on the card, not v-bind() —
   the modal's teleported subtree never receives component-root CSS vars. */
.picked-file {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--tpl-create-card-border);
  border-radius: 10px;
  background: var(--tpl-create-soft-surface);
}

.picked-file__icon {
  flex: none;
  color: var(--tpl-create-accent);
}
</style>
